(function () {
  // Use centralized constants (loaded from darkroom-constants.js)
  if (typeof DARKROOM === 'undefined') {
    console.error('DARKROOM constants not loaded. Ensure darkroom-constants.js is included before darkroom-booking.js');
    return;
  }

  /**
   * Defensive wrapper for try/catch blocks with consistent error logging
   * @param {Function} fn - Function to execute
   * @param {string} context - Context label for debugging
   * @param {*} fallback - Value to return if function throws
   * @returns {*} Result of fn() or fallback value
   */
  function safeTry(fn, context, fallback) {
    try {
      return fn();
    } catch (err) {
      console.warn('[Darkroom] Error in ' + context + ':', err);
      return fallback !== undefined ? fallback : null;
    }
  }

  /**
   * Safely access nested properties without throwing
   * @param {Object} obj - Object to access
   * @param {string} path - Property path (e.g., 'ShopifyAnalytics.meta.product')
   * @param {*} defaultValue - Default if path doesn't exist
   * @returns {*} Property value or defaultValue
   */
  function safeGet(obj, path, defaultValue) {
    if (obj == null || typeof path !== 'string') return defaultValue;
    var keys = path.split('.');
    var current = obj;
    for (var i = 0; i < keys.length; i++) {
      if (current == null) return defaultValue;
      current = current[keys[i]];
    }
    return current !== undefined ? current : defaultValue;
  }

  /**
   * Execute callback when DOM is ready
   * @param {Function} fn - Callback to execute
   */
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /**
   * How long the gift purchase waits for its cart token before submitting
   * anyway. Two round trips to Shopify comfortably fit; a hung request does
   * not hold up the sale.
   */
  var GIFT_TOKEN_WAIT_MS = 4000;

  /**
   * Resolves when `promise` settles either way, or when `ms` elapses —
   * whichever comes first, and never rejects.
   *
   * Used to gate a form submit on work that should happen first but must
   * never be able to block it: the caller gets on with the submit regardless,
   * and only the good case gets to wait.
   *
   * @param {Promise} promise - The work to wait on.
   * @param {number} ms - Cap on the wait.
   * @returns {Promise<void>} Always resolves.
   */
  function settleWithin(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      }
      var timer = setTimeout(function () {
        if (settled) return;
        console.error('[Darkroom] Gift token did not land within ' + ms + 'ms; submitting without one');
        finish();
      }, ms);
      promise.then(finish, function (err) {
        // A gift without a token is still a valid order — the recipient
        // contacts us and the shop books it by hand. Losing the sale would
        // be worse, so this never blocks the submit.
        console.error('[Darkroom] Could not mint a gift token; continuing without one', err);
        finish();
      });
    });
  }

  // Track the time between opening the calendar panel and availability being loaded.
  // The embed sets overlay.dataset.openedAt when it opens; when the embed iframe
  // posts a negdev:calendar/availability_loaded message, we compute the delta
  // and send a GA event if gtag is available (and a Plausible event as fallback).
  function handleAvailabilityLoaded(payload) {
    try {
      // Try to find the overlay element created by the embed (class .dr-overlay)
      var overlay = document.querySelector('.dr-overlay');
      var openedAt = overlay && overlay.dataset && overlay.dataset.openedAt ? Number(overlay.dataset.openedAt) : null;
      var loadedAt = payload && payload.loadedAt ? Number(payload.loadedAt) : Date.now();
      var deltaMs = openedAt ? Math.max(0, loadedAt - openedAt) : null;

      var scope = payload && payload.scope ? payload.scope : 'unknown';

      // Fire GA event if available
      if (window && typeof window.gtag === 'function') {
        try {
          // Include GA4-friendly parameter names alongside legacy UA keys so
          // parameters are available whether the site uses UA or GA4.
          var gtagParams = {
            event_category: 'calendar',
            event_label: scope,
            // legacy `value` (UA) and GA4-friendly `load_ms`
            value: deltaMs || undefined,
            load_ms: (deltaMs != null) ? deltaMs : undefined,
            scope: scope || undefined,
            non_interaction: true,
          };
          window.gtag('event', 'calendar_availability_loaded', gtagParams);
        } catch (e) {
          console.warn('[Darkroom] gtag call failed', e);
        }
      }

      // Also send to Plausible if configured
      if (window && typeof window.plausible === 'function') {
        try {
          window.plausible('calendar_availability_loaded', { props: { scope: scope, load_ms: deltaMs } });
        } catch (e) {
          // noop
        }
      }

      // availability_loaded handled; minimal logging in production
    } catch (err) {
      console.warn('[Darkroom] handleAvailabilityLoaded error', err);
    }
  }

  // Small helper to send analytics events to gtag and Plausible where available
  function sendAnalytics(eventName, gtagParams, plausibleEventName, plausibleProps) {
    try {
      if (window && typeof window.gtag === 'function') {
        var params = gtagParams || {};
        // Prefer beacon transport on navigation
        params.transport_type = params.transport_type || 'beacon';
        window.gtag('event', eventName, params);
      }
    } catch (e) {
      console.warn('[Darkroom] gtag send failed', e);
    }
    try {
      if (window && typeof window.plausible === 'function' && plausibleEventName) {
        window.plausible(plausibleEventName, plausibleProps ? { props: plausibleProps } : undefined);
      }
    } catch (e) {
      // noop
    }
  }

  // When the user clicks the "View dates" trigger, record open time and fire calendar_open
  function attachTriggerListeners() {
    try {
      var triggers = document.querySelectorAll(DARKROOM.SELECTORS.TRIGGER_BUTTON + ', .js-darkroom-calendar');
      triggers.forEach(function (el) {
        el.addEventListener('click', function (ev) {
          try {
            // Record a global opened timestamp; embed will also set overlay.dataset.openedAt
            window.__negdev_calendar_openedAt = Date.now();

            // Derive item id from hidden input if present
            var itemInput = document.querySelector(DARKROOM.SELECTORS.PRODUCT_ID_INPUT);
            var itemId = itemInput ? itemInput.value : undefined;

            // Try to get a scope hint if the overlay already exists (defensive)
            var overlayEl = document.querySelector('.dr-overlay');
            var detectedScope = (overlayEl && overlayEl.dataset && overlayEl.dataset.scope) ? overlayEl.dataset.scope : (window && window.DARKROOM_SCOPE ? window.DARKROOM_SCOPE : undefined);

            // Send both legacy UA keys and GA4-friendly parameters (scope, item_id)
            sendAnalytics('calendar_open', {
              event_category: 'calendar',
              event_label: itemId || document.title,
              scope: detectedScope || undefined,
              item_id: itemId || undefined,
              non_interaction: false,
            }, 'calendar_opened', { item_id: itemId });
          } catch (err) { console.warn('[Darkroom] trigger click handler failed', err); }
        }, { passive: true });
      });
    } catch (err) {
      console.warn('[Darkroom] attachTriggerListeners failed', err);
    }
  }

  // Listen for slot selection events from the embed and fire slot_selected analytics
  function attachSelectionListener() {
    try {
      document.addEventListener(DARKROOM.EVENTS.CALENDAR_SELECT_RANGE, function (e) {
        try {
          var payload = e && e.detail ? e.detail : null;
          if (!payload) return;
          var itemInput = document.querySelector(DARKROOM.SELECTORS.PRODUCT_ID_INPUT);
          var itemId = itemInput ? itemInput.value : undefined;

          sendAnalytics('slot_selected', {
            event_category: 'calendar',
            event_label: itemId || document.title,
            start_time: payload.startISO,
            end_time: payload.endISO,
            scope: payload.scope,
            non_interaction: false,
          }, 'slot_selected', { item_id: itemId, startISO: payload.startISO, endISO: payload.endISO });
        } catch (err) { console.warn('[Darkroom] slot selection handler failed', err); }
      });
    } catch (err) {
      console.warn('[Darkroom] attachSelectionListener failed', err);
    }
  }

  // Send add_to_cart event just before the form is submitted
  function attachAddToCartListener() {
    try {
      document.addEventListener('submit', function (e) {
        try {
          var form = e && e.target ? e.target : null;
          if (!form) return;
          // Only handle product add-to-cart forms
          if (!form.matches || !form.matches(DARKROOM.SELECTORS.FORM)) return;

          var itemInput = form.querySelector(DARKROOM.SELECTORS.PRODUCT_ID_INPUT) || document.querySelector(DARKROOM.SELECTORS.PRODUCT_ID_INPUT);
          var itemId = itemInput ? itemInput.value : undefined;
          var qtyInput = getQuantityInput(form);
          var qty = qtyInput ? Number(qtyInput.value || qtyInput.getAttribute('value') || 1) : 1;

          sendAnalytics('add_to_cart', {
            currency: DARKROOM.DEFAULTS.CURRENCY,
            value: undefined,
            items: [{ item_id: itemId, item_name: document.title, quantity: qty }],
          }, 'add_to_cart', { item_id: itemId, quantity: qty });
        } catch (err) { console.warn('[Darkroom] add_to_cart handler failed', err); }
      }, true); // use capture to run before potential navigation handlers
    } catch (err) {
      console.warn('[Darkroom] attachAddToCartListener failed', err);
    }
  }

  // Initialise listeners on DOM ready
  ready(function () {
    attachTriggerListeners();
    attachSelectionListener();
    attachAddToCartListener();
  });

  /**
   * Ensure a hidden booking property input exists on form
   * Creates one if it doesn't exist
   * @param {HTMLFormElement} form - Form element
   * @param {string} name - Property name (e.g., '_Booking Start')
   * @returns {HTMLInputElement|null} Hidden input element or null
   */
  function ensureProp(form, name) {
    if (!form || typeof name !== 'string') return null;
    var sel = 'input[name="properties[' + name + ']"]';
    var els = form.querySelectorAll(sel);
    
    // Remove all but the first (in case duplicates exist from previous bookings)
    if (els.length > 1) {
      for (var i = 1; i < els.length; i++) {
        els[i].remove();
      }
    }
    
    // Return first element, or create new one if none exist
    var el = form.querySelector(sel);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = 'properties[' + name + ']';
      form.appendChild(el);
    }
    return el;
  }

  /**
   * Find the submit button in a form
   * @param {HTMLFormElement} form - Form element
   * @returns {HTMLButtonElement|null} Submit button or null
   */
  function findSubmit(form) {
    return form && form.querySelector(DARKROOM.SELECTORS.SUBMIT_BUTTON);
  }

  /**
   * Show error message to user
   * Sets error wrapper visibility and updates message text
   * Accessibility: Ensures aria-live region updates are announced
   * @param {HTMLFormElement} form - Form element
   * @param {string} msg - Error message to display
   */
  function showError(form, msg) {
    if (!form) return;
    var wrap = form.querySelector(DARKROOM.SELECTORS.ERROR_WRAPPER);
    var span = form.querySelector(DARKROOM.SELECTORS.ERROR_MESSAGE);
    if (wrap) {
      wrap.hidden = false;
      // Ensure aria-live is set for accessibility (screen reader announcement)
      if (!wrap.getAttribute('aria-live')) {
        wrap.setAttribute('aria-live', 'polite');
        wrap.setAttribute('role', 'alert');
      }
    }
    if (span) span.textContent = msg || DARKROOM.DEFAULTS.ERROR_MESSAGE;
  }

  /**
   * Clear/hide error message
   * @param {HTMLFormElement} form - Form element
   */
  function clearError(form) {
    if (!form) return;
    var wrap = form.querySelector(DARKROOM.SELECTORS.ERROR_WRAPPER);
    if (wrap) wrap.hidden = true;
  }

  /**
   * Enable or disable form submit button
   * Updates both disabled state and aria-disabled for accessibility
   * @param {HTMLFormElement} form - Form element
   * @param {boolean} enabled - Whether button should be enabled
   */
  function setSubmitEnabled(form, enabled) {
    var btn = findSubmit(form);
    if (!btn) return;
    btn.disabled = !enabled;
    btn.setAttribute('aria-disabled', (!enabled).toString());
    // Hide spinner when disabled
    var spinner = btn.querySelector(DARKROOM.SELECTORS.SPINNER);
    if (spinner) spinner.classList.add('hidden');
  }

  /**
   * Check if a valid booking selection exists in session storage
   * Valid selection must have both startISO and endISO
   * @returns {boolean} True if valid selection exists
   */
  function hasValidSelection() {
    return safeTry(function () {
      var sel = JSON.parse(sessionStorage.getItem(DARKROOM.STORAGE_KEY) || '{}');
      return Boolean(sel && sel.startISO && sel.endISO);
    }, 'hasValidSelection', false);
  }

  /**
   * Derive hour quantity from ISO time strings
   * @param {string} startISO - Start time (ISO 8601)
   * @param {string} endISO - End time (ISO 8601)
   * @returns {number} Quantity in hours (minimum 1)
   */
  function deriveQuantityFromISO(startISO, endISO) {
    if (typeof startISO !== 'string' || typeof endISO !== 'string') return 1;
    var quantity = 1;
    if (startISO && endISO) {
      var startMs = Date.parse(startISO);
      var endMs = Date.parse(endISO);
      if (!isNaN(startMs) && !isNaN(endMs)) {
        var ms = endMs - startMs;
        quantity = Math.max(1, Math.round(ms / (60 * 60 * 1000)));
      }
    }
    return quantity;
  }

  /**
   * Find quantity input field
   * Checks form directly, then via form attribute, then document
   * @param {HTMLFormElement} form - Form element
   * @returns {HTMLInputElement|null} Quantity input or null
   */
  function getQuantityInput(form) {
    if (!form) return null;
    // Try direct querySelector first (most common)
    var q = form.querySelector('[name="quantity"]');
    if (q) {
      console.log('[Darkroom] Found quantity input in form', { inputId: q.id, inputName: q.name, currentValue: q.value });
      return q;
    }
    // Try finding by form attribute if form has an id
    if (form.id) {
      q = document.querySelector('[name="quantity"][form="' + form.id + '"]');
      if (q) {
        console.log('[Darkroom] Found quantity input by form attribute', { formId: form.id, inputId: q.id });
        return q;
      }
    }
    // Fall back to document query (shouldn't be needed but defensive)
    var fallback = document.querySelector('[name="quantity"]');
    if (fallback) {
      console.log('[Darkroom] Found quantity input via document fallback');
    } else {
      console.warn('[Darkroom] NO quantity input found anywhere in page');
    }
    return fallback;
  }

  /**
   * Clear any prior booking selection state on page load
   * Removes sessionStorage entry and clears form properties
   * @param {Object} opts - Options object
   * @param {string} [opts.formSelector] - Form selector (defaults to DARKROOM.SELECTORS.FORM)
   * @param {string} [opts.propertyLabel] - Visible property name
   * @param {string} [opts.propertyStartKey] - Start time property name
   * @param {string} [opts.propertyEndKey] - End time property name
   * @param {string} [opts.propertyScopeKey] - Scope property name
   * @param {string} [opts.propertyHoursKey] - Hours property name
   */
  function resetSelection(opts) {
    opts = opts || {};
    var form = document.querySelector(opts.formSelector || DARKROOM.SELECTORS.FORM);
    if (!form) {
      console.debug('[Darkroom] resetSelection: form not found');
      return;
    }

    safeTry(function () { sessionStorage.removeItem(DARKROOM.STORAGE_KEY); }, 'sessionStorage.removeItem');

    // Note: Selection display element (.js-darkroom-selection) intentionally not used
    // Selection information is shown in the embed panel and Shopify cart, not on product page

    var keys = [
      (opts.propertyLabel || DARKROOM.PROPERTIES.LABEL),
      (opts.propertyStartKey || DARKROOM.PROPERTIES.START),
      (opts.propertyEndKey || DARKROOM.PROPERTIES.END),
      (opts.propertyScopeKey || DARKROOM.PROPERTIES.SCOPE),
      (opts.propertyHoursKey || DARKROOM.PROPERTIES.HOURS)
    ];
    keys.forEach(function(name){
      if (typeof name !== 'string') return;
      var sel = 'input[name="properties[' + name + ']"]';
      var el = form.querySelector(sel);
      if (el) el.value = '';
    });

    clearError(form);
    setSubmitEnabled(form, false);
  }

  /**
   * Apply a booking selection to the form
   * Sets all property fields and triggers events
   * @param {Object} payload - Booking payload from embed
   * @param {string} payload.startISO - Start time (ISO 8601)
   * @param {string} payload.endISO - End time (ISO 8601)
   * @param {string} payload.scope - Booking scope (darkroom, google-event, etc)
   * @param {string} payload.display - Human-readable display text
   * @param {number} [payload.quantity] - Explicit quantity (optional)
   * @param {string} [payload.eventId] - Event ID for google-event scope
   * @param {string} [payload.eventTitle] - Event title for google-event scope
   * @param {number} [payload.maxQuantity] - Max capacity for google-event scope
   * @param {Object} opts - Options object
   * @param {string} [opts.formSelector] - Form selector
   * @param {string} [opts.propertyLabel] - Visible property name
   * @param {string} [opts.propertyStartKey] - Start property name
   * @param {string} [opts.propertyEndKey] - End property name
   * @param {string} [opts.propertyScopeKey] - Scope property name
   * @param {string} [opts.propertyHoursKey] - Hours property name
   */
  function applySelection(payload, opts) {
    opts = opts || {};
    var form = document.querySelector(opts.formSelector || DARKROOM.SELECTORS.FORM);
    if (!form || !payload) {
      console.debug('[Darkroom] applySelection: form or payload missing');
      return;
    }

    // Visible property shown to the customer
    var kLabelVisible = opts.propertyLabel || DARKROOM.PROPERTIES.LABEL;
    // Hidden machine properties (underscore-prefixed so Shopify hides them in cart/checkout)
    var kStart = opts.propertyStartKey || DARKROOM.PROPERTIES.START;
    var kEnd   = opts.propertyEndKey   || DARKROOM.PROPERTIES.END;
    var kScope = opts.propertyScopeKey || DARKROOM.PROPERTIES.SCOPE;
    var kHours = opts.propertyHoursKey || DARKROOM.PROPERTIES.HOURS;

    // Use quantity from payload if available, otherwise derive from ISO times
    var quantityExact = payload.quantity || deriveQuantityFromISO(payload.startISO, payload.endISO);
    console.log('[Darkroom] applySelection - quantityExact calculated', { payloadQuantity: payload.quantity, quantityExact: quantityExact, scope: payload.scope, participantCount: payload.participantCount });

    var labelProp = ensureProp(form, kLabelVisible);
    if (labelProp) labelProp.value = payload.display || '';
    
    var startProp = ensureProp(form, kStart);
    if (startProp) startProp.value = payload.startISO || '';
    
    var endProp = ensureProp(form, kEnd);
    if (endProp) endProp.value = payload.endISO || '';
    
    var scopeProp = ensureProp(form, kScope);
    if (scopeProp) scopeProp.value = payload.scope || '';
    
    // For non-google-event bookings, store hours; for google-event, _Participant Count is used instead
    if (payload.scope !== DARKROOM.SCOPE.GOOGLE_EVENT) {
      var hoursProp = ensureProp(form, kHours);
      if (hoursProp) hoursProp.value = String(quantityExact);
    }

    // For google-event bookings, also pass through event metadata (underscore prefix hides from customer)
    if (payload.scope === DARKROOM.SCOPE.GOOGLE_EVENT) {
      if (payload.eventId) {
        var eventIdProp = ensureProp(form, DARKROOM.PROPERTIES.EVENT_ID);
        if (eventIdProp) eventIdProp.value = payload.eventId;
      }
      if (payload.eventTitle) {
        var eventTitleProp = ensureProp(form, DARKROOM.PROPERTIES.EVENT_TITLE);
        if (eventTitleProp) eventTitleProp.value = payload.eventTitle;
      }
      if (payload.maxQuantity) {
        var maxQtyProp = ensureProp(form, DARKROOM.PROPERTIES.MAX_QUANTITY);
        if (maxQtyProp) maxQtyProp.value = String(payload.maxQuantity);
      }
      // Store participant count separately (webhook will use this to add attendees)
      if (payload.participantCount) {
        var participantProp = ensureProp(form, DARKROOM.PROPERTIES.PARTICIPANT_COUNT);
        if (participantProp) participantProp.value = String(payload.participantCount);
      }
    }

    var qty = getQuantityInput(form);
    if (qty) {
      var oldValue = qty.value;
      var newValue = String(quantityExact);
      qty.value = newValue;
      qty.setAttribute('value', newValue);
      console.log('[Darkroom] Quantity input updated', { oldValue: oldValue, newValue: newValue, propertyAfter: qty.value, attributeAfter: qty.getAttribute('value') });
      safeTry(function () { qty.dispatchEvent(new Event('input', { bubbles: true })); }, 'qty.dispatchEvent(input)');
      safeTry(function () { qty.dispatchEvent(new Event('change', { bubbles: true })); }, 'qty.dispatchEvent(change)');
      var qtyBlock = qty.closest(DARKROOM.SELECTORS.QUANTITY_BLOCK) || qty.parentElement;
      if (qtyBlock) qtyBlock.style.display = 'none';
    } else {
      console.error('[Darkroom] CRITICAL: Quantity input not found!');
    }

    // Note: Selection display element (.js-darkroom-selection) intentionally not used
    // Selection information is shown in the embed panel and Shopify cart, not on product page

    safeTry(function () { sessionStorage.setItem(DARKROOM.STORAGE_KEY, JSON.stringify(payload)); }, 'sessionStorage.setItem');

    clearError(form);
    setSubmitEnabled(form, true);

  safeTry(function () { document.dispatchEvent(new CustomEvent(DARKROOM.EVENTS.BOOKING_SELECTED, { detail: payload })); }, 'document.dispatchEvent(booking-selected)');
  }

  /**
   * Guard form submission
   * Validates that a booking selection exists before allowing submit
   * Prevents form submission and shows error if validation fails
   * Gift purchases bypass this check (no booking selection needed)
   * @param {Event} e - Submit event
   * @param {Object} opts - Options object
   * @param {string} [opts.propertyHoursKey] - Hours property name
   */
  function onSubmitGuard(e, opts) {
    if (!e || !e.target) return;
    var form = e.target;

    // Gift purchases don't require a booking selection
    var giftProp = form.querySelector('input[name="properties[_is_purchased_as_gift]"]');
    if (giftProp && giftProp.value === 'true') {
      return; // Allow gift submission
    }

    if (!hasValidSelection()) {
      e.preventDefault();
      setSubmitEnabled(form, false);
      showError(form, DARKROOM.DEFAULTS.ERROR_MESSAGE_SUBMIT);
      return;
    }

    var sel = safeTry(function () { 
      return JSON.parse(sessionStorage.getItem(DARKROOM.STORAGE_KEY) || '{}'); 
    }, 'JSON.parse(booking)', {});
    
    var quantity = null;
    if (sel && typeof sel.quantity === 'number') {
      quantity = sel.quantity;
    } else if (sel && sel.startISO && sel.endISO) {
      quantity = deriveQuantityFromISO(sel.startISO, sel.endISO);
    } else {
      var qEl = getQuantityInput(form);
      if (qEl && qEl.value) {
        var parsed = parseInt(qEl.value, 10);
        if (!isNaN(parsed)) quantity = Math.max(1, parsed);
      }
    }
    if (quantity == null) quantity = 1;
    
    var hoursProp = ensureProp(form, (opts && opts.propertyHoursKey) || DARKROOM.PROPERTIES.HOURS);
    if (hoursProp) hoursProp.value = String(quantity);
  }

  // Listen for availability_loaded custom events dispatched by the embed panel
  safeTry(function () {
    document.addEventListener('negdev:calendar/availability_loaded', function (e) {
      var payload = e && e.detail ? e.detail : null;
      handleAvailabilityLoaded(payload);
    });
  }, 'addEventListener(availability_loaded)');

  /**
   * Main initialization
   * Sets up embed, event listeners, and form validation
   */
  ready(function () {
    if (!window[DARKROOM.METAFIELDS.SHOW_AVAILABILITY_PREVIEW]) return;

    var defaultScope = (window.DARKROOM_SCOPE || DARKROOM.DEFAULT_SCOPE);
    console.log(DARKROOM.LOG.SCOPE_DETECTION, {
      show_availability_preview: window[DARKROOM.METAFIELDS.SHOW_AVAILABILITY_PREVIEW],
      google_event_name: window[DARKROOM.METAFIELDS.GOOGLE_EVENT_NAME],
      max_quantity: window[DARKROOM.METAFIELDS.MAX_QUANTITY],
      DARKROOM_SCOPE: window.DARKROOM_SCOPE,
      defaultScope_before: defaultScope
    });
      
    safeTry(function () {
      // Infer scope from product google_event_name metafield (takes precedence)
      if (window[DARKROOM.METAFIELDS.GOOGLE_EVENT_NAME]) {
        defaultScope = DARKROOM.SCOPE.GOOGLE_EVENT;
        console.log(DARKROOM.LOG.SCOPE_SET, 'google-event');
      } else if (!window.DARKROOM_SCOPE && Array.isArray(window.customerTags)) {
        defaultScope = window.customerTags.indexOf(DARKROOM.SCOPE.UNSUPERVISED) >= 0 ? DARKROOM.SCOPE.UNSUPERVISED : DARKROOM.SCOPE.SUPERVISED;
        console.log(DARKROOM.LOG.SCOPE_SET + ' from customer tags:', defaultScope);
      }
    }, 'scope detection');

    // Initialize embed overlay with scope and price (cents) + currency
    safeTry(function () {
      console.log(DARKROOM.LOG.INIT, {
        defaultScope: defaultScope,
        getScope: defaultScope || window.DARKROOM_SCOPE || DARKROOM.DEFAULT_SCOPE,
        getGoogleEventName: window[DARKROOM.METAFIELDS.GOOGLE_EVENT_NAME] || ''
      });
      
      DarkroomCalendar.init({
        trigger: DARKROOM.SELECTORS.TRIGGER_BUTTON,
        getScope: function () { return defaultScope || window.DARKROOM_SCOPE || DARKROOM.DEFAULT_SCOPE; },
        src: DARKROOM.EMBED.BASE_URL,
        getGoogleEventName: function () { return window[DARKROOM.METAFIELDS.GOOGLE_EVENT_NAME] || ''; },
        getMaxQuantity: function () { return window[DARKROOM.METAFIELDS.MAX_QUANTITY] || 0; },
        // Expose can_be_gifted from product metafields to the embed wrapper
        getCanBeGifted: function () { return typeof window.product_metafields_can_be_gifted !== 'undefined' ? window.product_metafields_can_be_gifted : false; },
        formSelector: DARKROOM.SELECTORS.FORM,
        propertyLabel: DARKROOM.PROPERTIES.LABEL,
        propertyStartKey: DARKROOM.PROPERTIES.START,
        propertyEndKey: DARKROOM.PROPERTIES.END,
        propertyScopeKey: DARKROOM.PROPERTIES.SCOPE,
        propertyHoursKey: DARKROOM.PROPERTIES.HOURS,
        iframeOrigin: DARKROOM.EMBED.IFRAME_ORIGIN,

        // Unit price (in cents) and currency for panel footer totals
        getPrice: function () {
          var scope = defaultScope || window.DARKROOM_SCOPE || DARKROOM.DEFAULT_SCOPE;
          // For google-event scope, always use variant pricing (not per-hour)
          if (scope === DARKROOM.SCOPE.GOOGLE_EVENT) {
            // Fall through to variant pricing logic below
          } else if (scope === DARKROOM.SCOPE.UNSUPERVISED) {
            return DARKROOM.DEFAULTS.UNSUPERVISED_PRICE_CENTS;
          }
          
          return safeTry(function () {
            var form = document.querySelector(DARKROOM.SELECTORS.FORM);
            if (!form) { 
              console.debug(DARKROOM.LOG.GET_PRICE_NO_FORM); 
              return 0; 
            }
            
            var idInput = form.querySelector(DARKROOM.SELECTORS.PRODUCT_ID_INPUT);
            var vid = idInput && idInput.value ? parseInt(idInput.value, 10) : null;

            var jsonEl =
              document.querySelector(DARKROOM.SELECTORS.VARIANT_RADIOS + ' ' + DARKROOM.SELECTORS.VARIANT_JSON) ||
              document.querySelector(DARKROOM.SELECTORS.VARIANT_SELECTS + ' ' + DARKROOM.SELECTORS.VARIANT_JSON);

            // Try ShopifyAnalytics first (most reliable)
            var priceA = safeGet(window, 'ShopifyAnalytics.meta.product.variants', null);
            if (priceA && Array.isArray(priceA)) {
              var vA = priceA.find(function (x) { return x && x.id === vid; });
              if (vA && typeof vA.price === 'number') {
                console.debug(DARKROOM.LOG.GET_PRICE_SHOPIFY_ANALYTICS, { vid: vid, priceCents: vA.price });
                return vA.price;
              }
            }

            // Fall back to variant JSON script tag
            if (!jsonEl) { 
              console.debug(DARKROOM.LOG.GET_PRICE_NO_VARIANTS); 
              return 0; 
            }

            var variants = JSON.parse(jsonEl.textContent || '[]');
            var v = variants && Array.isArray(variants) ? variants.find(function (x) { return x && x.id === vid; }) : null;
            var price = v && typeof v.price === 'number' ? v.price : 0; // cents
            console.debug(DARKROOM.LOG.GET_PRICE_VARIANTS_JSON, { vid: vid, priceCents: price });
            return price;
           }, 'getPrice', 0);
         },
         currency: safeGet(window, 'Shopify.currency.active', DARKROOM.DEFAULTS.CURRENCY)
       });
    }, 'DarkroomCalendar init');

    // Reset to default state on load
    resetSelection({
      formSelector: DARKROOM.SELECTORS.FORM,
      propertyLabel: DARKROOM.PROPERTIES.LABEL,
      propertyStartKey: DARKROOM.PROPERTIES.START,
      propertyEndKey: DARKROOM.PROPERTIES.END,
      propertyScopeKey: DARKROOM.PROPERTIES.SCOPE,
      propertyHoursKey: DARKROOM.PROPERTIES.HOURS
    });

    // Apply selection when the embed posts it (calendar select-range event)
    document.addEventListener(DARKROOM.EVENTS.CALENDAR_SELECT_RANGE, function (e) {
      applySelection((e && e.detail) || {}, {
        formSelector: DARKROOM.SELECTORS.FORM,
        propertyLabel: DARKROOM.PROPERTIES.LABEL,
        propertyStartKey: DARKROOM.PROPERTIES.START,
        propertyEndKey: DARKROOM.PROPERTIES.END,
        propertyScopeKey: DARKROOM.PROPERTIES.SCOPE,
        propertyHoursKey: DARKROOM.PROPERTIES.HOURS
      });
    });

      // Listen for postMessage from embedded calendar (google-event bookings and gift purchases)
      window.addEventListener('message', function (e) {
        // Handle explicit booking messages from the embed
        if (!e.data || e.data.type !== DARKROOM.EVENTS.CALENDAR_BOOK || !e.data.payload) return;
        
        var payload = e.data.payload || {};
        
        // Gift purchases: add property and submit without date fields
        if (payload._is_purchased_as_gift) {
          var form = document.querySelector(DARKROOM.SELECTORS.FORM);
          if (form) {
            // Use underscore prefix to hide from customer
            var giftProp = ensureProp(form, '_is_purchased_as_gift');
            if (giftProp) {
              giftProp.value = 'true';
              console.log('[Darkroom] Set _is_purchased_as_gift property', { name: giftProp.name, value: giftProp.value });
            }

            // Gifts have no date yet — remove any visible "Date" property left
            // over from a prior selection so only the "Gift purchase" note shows
            // at checkout. Keeps parity with the dropdown gift flow.
            form.querySelectorAll('input[name="properties[' + DARKROOM.PROPERTIES.LABEL + ']"]')
              .forEach(function (inp) { inp.remove(); });

            // Set a public, human-readable property so the buyer clearly sees
            // what's happening at checkout (key has no underscore, so it shows).
            var giftLabelProp = ensureProp(form, 'Gift purchase');
            if (giftLabelProp) giftLabelProp.value = 'Choose a workshop date after checkout';

            // Add through /cart/add.js rather than submitting the form, so the
            // page is still here afterwards and the gift token can be minted
            // onto a cart that now HAS the item in it.
            //
            // Shopify silently drops attributes written to an empty cart, so
            // minting before the add lost the token every time and the
            // confirmation email went out with no booking link. A native submit
            // navigates away, leaving no moment in which to mint correctly —
            // hence the fetch. See gift-token.js.
            fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
              .then(function (resp) {
                if (!resp.ok) throw new Error('Add to cart failed');
                return resp.json();
              })
              .then(function () {
                var ensure = window.NegDevGiftToken && window.NegDevGiftToken.ensure;
                var minted = ensure
                  ? ensure()
                  : Promise.reject(new Error('gift-token.js not loaded'));
                // settleWithin never rejects and gives up after a cap, so a
                // token that fails or hangs cannot strand the buyer on this
                // page — and cannot swallow a failed add above, which is
                // handled by the catch at the end of the chain.
                return settleWithin(minted, GIFT_TOKEN_WAIT_MS);
              })
              .then(function () {
                window.location.href = '/cart';
              })
              .catch(function (err) {
                console.error('[Darkroom] Gift purchase add to cart failed', err);
                alert('Could not add this gift to your cart. Please try again.');
              });
          } else {
            console.warn('[Darkroom] Gift purchase: form not found with selector', DARKROOM.SELECTORS.FORM);
          }
          return;
        }
        
        // Regular booking: apply selection with date fields
        applySelection(payload, {
          formSelector: DARKROOM.SELECTORS.FORM,
          propertyLabel: DARKROOM.PROPERTIES.LABEL,
          propertyStartKey: DARKROOM.PROPERTIES.START,
          propertyEndKey: DARKROOM.PROPERTIES.END,
          propertyScopeKey: DARKROOM.PROPERTIES.SCOPE,
          propertyHoursKey: DARKROOM.PROPERTIES.HOURS
        });
      });

    // Guard submit - prevent form submission without valid booking
    var form = document.querySelector(DARKROOM.SELECTORS.FORM);
    if (form) {
      form.addEventListener('submit', function (e) {
        onSubmitGuard(e, { propertyHoursKey: DARKROOM.PROPERTIES.HOURS });
      }, { capture: true });
    }
  });
})();
